import React, { useEffect, useRef, useState } from 'react';
import { PersonalForm, StudyForm, PlaceForm, JobAreaForm, WorkingTimeForm, OfficeForm, ContactForm, PasswordForm, CVForm, PrivacyForm } from 'components/Form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FeedbackModal from 'components/FeedbackModal';
import PageHeading from 'elements/PageHeading';
import {
    PiBriefcaseDuotone,
    PiBuildingsDuotone,
    PiClockDuotone,
    PiEnvelopeSimpleDuotone,
    PiFileTextDuotone,
    PiLockKeyDuotone,
    PiMapPinDuotone,
    PiShieldCheckDuotone,
    PiStudentDuotone,
    PiUserCircleDuotone,
} from 'react-icons/pi';

export default function RegistrationPage(props) {
    const navigate = useNavigate();
    const registrationData = useRef({
        workDtoList: {},
        langDtoList: null,
        altPNDtoList: null,
        dto: {},
    });

    const [registrationError, setRegistrationError] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState({
        open: false,
        title: '',
        message: '',
    });

    const mergeRegistrationData = (newData) => {
        registrationData.current = {
            ...registrationData.current,
            ...newData,
            dto: {
                ...registrationData.current.dto,
                ...(newData.value || newData.dto || {}),
            },
        };

        delete registrationData.current.value;
        delete registrationData.current.config;
    };

    const buildRegistrationPayload = () => {
        const raw = registrationData.current;

        const dto = { ...(raw.dto || {}) };

        const workingTime = dto.workingTime || raw.workDtoList?.workingTime || [];

        delete dto.workingTime;

        return {
            workDtoList: {
                workingTime,
            },
            langDtoList: null,
            altPNDtoList: null,
            dto: {
                ...dto,

                birthDate: dto.birthDate
                    ? `${dto.birthDate}T00:00:00.000+01:00`
                    : null,

                officeList: (dto.officeList || []).map(Number),
                workCountyList: (dto.workCountyList || []).map(Number),
                workSettlementList: (dto.workSettlementList || []).map(Number),
                workJobAreaList: (dto.workJobAreaList || []).map(Number),

                serviceProviderNumber: Number(dto.serviceProviderNumber),
                countryCode: Number(dto.countryCode || 1),

                alternativePhoneNumberList: [],

                photoPercent: false,
                contactPercent: false,
                addressPercent: false,
                studiesPercent: false,
                schoolPercent: false,
                languagePercent: false,
                drivingPercent: false,
                contractingPercent: false,
                hasCar: false,
                updatedGoldcardID: false,
                updatedTaxId: false,
                updatedEmail: false,
                updatedBirthDate: false,
                updatedStudentCardNumber: false,
                updatedAddress: false,
                updatedTempAddress: false,
                doqTermsAccepted: false,
                updatedDOQId: false,
            },
        };
    };

    const handleRegister = async () => {
        try {
            setIsRegistering(true);
            setRegistrationError(null);

            const body = buildRegistrationPayload();

            console.log('Final registration payload:', body);

            const response = await axios.post(
                global.backingsEndpoint + 'employeeUserControl/registration',
                body
            );

            if (response.data?.successful === false) {
                setRegistrationError(response.data.properties || null);

                setFeedbackModal({
                    open: true,
                    title: 'Sikertelen regisztráció',
                    message: getBackendMessage(response.data.properties),
                });

                return;
            }

            const email = registrationData.current.dto.email;
            const phoneNumber = registrationData.current.dto.phoneNumber;

            navigate(
                `/regisztracio-visszaigazolas?email=${encodeURIComponent(email)}&phoneNumber=${encodeURIComponent(phoneNumber)}`
            );
        } catch (error) {
            console.error('Registration failed:', error);
            setFeedbackModal({
                open: true,
                title: 'Sikertelen regisztráció',
                message: 'A regisztrációt most nem sikerült elküldeni. Kérjük, próbáld újra néhány perc múlva.',
            });
        } finally {
            setIsRegistering(false);
        }
    };

    const getBackendMessage = (properties) => {
        if (!properties) return 'Ismeretlen hiba történt.';

        const firstField = Object.keys(properties)[0];
        const firstError = properties[firstField]?.[0];

        return firstError?.message || 'Ismeretlen hiba történt.';
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, []);

    return (
        <div className='relative min-h-screen gradient-green px-site py-site'>
            <div
                className='absolute inset-0 bg-cover bg-center bg-no-repeat'
                style={{
                backgroundImage: 'url(/assets/images/background/pattern-opacity.png)',
                }}
            />

            <div className='relative z-10 mx-auto w-full max-w-boxed flex flex-col gap-10'>
                <PageHeading
                    title='Regisztráció'
                    subTitle={
                        <div className='text-sm sm:text-base leading-relaxed text-white/90 space-y-2'>
                            <p>Csatlakozz a Fürge Diákhoz, és legyél az egyik legnagyobb magyar diákközösség tagja!</p>
                            <p>Segítünk eligazodni a munka világában, és képviseljük az érdekeidet.</p>
                            <p className='font-bold text-white'>Tarts velünk Te is!</p>
                        </div>
                    }
                />

                    <div className='registration-form flex min-w-0 flex-col gap-6'>
                        <PersonalForm
                            variant='registration'
                            icon={<PiUserCircleDuotone />}
                            color = 'text-neutral-900'
                            backgroundColor = 'gradient-green'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <StudyForm
                            variant='registration'
                            icon={<PiStudentDuotone />}
                            color = 'text-neutral-900'
                            backgroundColor = 'gradient-green'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <PlaceForm
                            variant='registration'
                            icon={<PiMapPinDuotone />}
                            color = 'text-neutral-900'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <JobAreaForm
                            variant='registration'
                            icon={<PiBriefcaseDuotone />}
                            color = 'text-neutral-900'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <WorkingTimeForm
                            variant='registration'
                            icon={<PiClockDuotone />}
                            color = 'text-neutral-900'
                            backgroundColor = 'gradient-green'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <OfficeForm
                            variant='registration'
                            icon={<PiBuildingsDuotone />}
                            color = 'text-neutral-900'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <ContactForm
                            variant='registration'
                            icon={<PiEnvelopeSimpleDuotone />}
                            color = 'text-neutral-900'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <PasswordForm
                            variant='registration'
                            icon={<PiLockKeyDuotone />}
                            color = 'text-neutral-900'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <CVForm
                            variant='registration'
                            icon={<PiFileTextDuotone />}
                            color = 'text-neutral-900'
                            backgroundColor = 'gradient-green'
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <PrivacyForm
                            variant='registration'
                            icon={<PiShieldCheckDuotone />}
                            color = 'text-neutral-900'
                            backgroundColor = 'gradient-green'
                            borderColor="border-green-500"
                            onValueChange={mergeRegistrationData}
                            error={null}
                        />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleRegister}
                                disabled={isRegistering}
                                className="form-action-button w-full bg-lime-500 text-white shadow-lg hover:-translate-y-0.5 hover:bg-lime-600 hover:shadow-xl focus-visible:ring-white/30 sm:w-auto sm:min-w-56"
                            >
                                {isRegistering ? 'Elküldöm...' : 'Regisztráció elküldése'}
                            </button>
                        </div>
                </div>
            </div>
            <FeedbackModal
                open={feedbackModal.open}
                tone="error"
                title={feedbackModal.title}
                message={feedbackModal.message}
                actionLabel="Vissza az űrlaphoz"
                onClose={() =>
                    setFeedbackModal({
                        open: false,
                        title: '',
                        message: '',
                    })
                }
            />
        </div>
    );
}
